import React, { useState } from 'react';
import { Stack, Text, Group, Box, Card, TextInput, Button, ScrollArea, Stepper } from '@mantine/core';
import { User, Phone, Mail, Send, Gift, Users, CheckCircle } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
import type { Referral, ReferralStage } from '../types';
// Mock data
const mockReferrals: Referral[] = [{
  id: '1',
  name: 'Mohammed Hassan',
  phone: '+971 55 123 4567',
  email: 'mohammed@example.com',
  stage: 'deal-won',
  submittedAt: '2023-10-15'
}, {
  id: '2',
  name: 'Sara Ahmed',
  phone: '+971 50 987 6543',
  stage: 'negotiation',
  submittedAt: '2023-12-01'
}, {
  id: '3',
  name: 'Khalid Omar',
  phone: '+971 52 456 7890',
  stage: 'site-visit',
  submittedAt: '2024-01-05'
}, {
  id: '4',
  name: 'Fatima Ali',
  phone: '+971 54 321 0987',
  stage: 'contacted',
  submittedAt: '2024-01-12'
}, {
  id: '5',
  name: 'Omar Youssef',
  phone: '+971 56 789 0123',
  stage: 'new-lead',
  submittedAt: '2024-01-18'
}];
const stageOrder: ReferralStage[] = ['new-lead', 'contacted', 'site-visit', 'negotiation', 'deal-won'];
const stageLabels: Record<ReferralStage, string> = {
  'new-lead': 'New Lead',
  contacted: 'Contacted',
  'site-visit': 'Site Visit',
  negotiation: 'Negotiation',
  'deal-won': 'Deal Won'
};
export function ReferralTracker() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const referralsByStage = stageOrder.map(stage => ({
    stage,
    label: stageLabels[stage],
    count: mockReferrals.filter(r => r.stage === stage).length
  }));
  const totalReferrals = mockReferrals.length;
  const successfulReferrals = mockReferrals.filter(r => r.stage === 'deal-won').length;
  const pendingReferrals = totalReferrals - successfulReferrals;
  const handleSubmit = () => {
    console.log('Submit referral:', formData);
    setFormData({
      name: '',
      phone: '',
      email: ''
    });
  };
  const getStageIndex = (stage: ReferralStage) => stageOrder.indexOf(stage);
  return <ScrollArea h="100%" offsetScrollbars data-id="element-447">
      <Stack gap="lg" pb={100} data-id="element-448">
        {/* Header */}
        <Box data-id="element-449">
          <Text fw={700} size="xl" c="#1a2332" mb={4} data-id="element-450">
            Referral Program
          </Text>
          <Text size="sm" c="#64748b" data-id="element-451">
            Earn rewards by referring friends & family
          </Text>
        </Box>

        {/* Reward Banner */}
        <Card p="lg" radius="md" style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
      }} data-id="element-452">
          <Group gap="md" data-id="element-453">
            <Box p={12} style={{
            backgroundColor: 'rgba(255,255,255,0.2)',
            borderRadius: 12
          }} data-id="element-454">
              <Gift size={28} color="white" data-id="element-455" />
            </Box>
            <Stack gap={2} data-id="element-456">
              <Text fw={700} size="lg" c="white" data-id="element-457">
                Earn AED 10,000
              </Text>
              <Text size="sm" c="rgba(255,255,255,0.9)" data-id="element-458">
                For every successful referral that closes a deal
              </Text>
            </Stack>
          </Group>
        </Card>

        {/* Stats */}
        <Group grow gap="sm" data-id="element-459">
          <Card p="md" radius="md" withBorder data-id="element-460">
            <Group gap="sm" data-id="element-461">
              <Users size={20} color="#3b82f6" data-id="element-462" />
              <Stack gap={0} data-id="element-463">
                <Text size="xs" c="#64748b" data-id="element-464">
                  Total Referrals
                </Text>
                <Text fw={700} size="lg" c="#1a2332" data-id="element-465">
                  {totalReferrals}
                </Text>
              </Stack>
            </Group>
          </Card>
          <Card p="md" radius="md" withBorder data-id="element-466">
            <Group gap="sm" data-id="element-467">
              <CheckCircle size={20} color="#10b981" data-id="element-468" />
              <Stack gap={0} data-id="element-469">
                <Text size="xs" c="#64748b" data-id="element-470">
                  Successful
                </Text>
                <Text fw={700} size="lg" c="#10b981" data-id="element-471">
                  {successfulReferrals}
                </Text>
              </Stack>
            </Group>
          </Card>
        </Group>

        {/* Submit Referral Form */}
        <Card shadow="sm" p="lg" radius="md" withBorder data-id="element-472">
          <Stack gap="md" data-id="element-473">
            <Text fw={700} size="md" c="#1a2332" data-id="element-474">
              Submit a New Referral
            </Text>

            <TextInput label="Full Name" placeholder="Enter referral's name" leftSection={<User size={16} color="#64748b" data-id="element-476" />} value={formData.name} onChange={e => setFormData({
            ...formData,
            name: e.target.value
          })} radius="md" data-id="element-475" />

            <TextInput label="Phone Number" placeholder="+971 50 XXX XXXX" leftSection={<Phone size={16} color="#64748b" data-id="element-478" />} value={formData.phone} onChange={e => setFormData({
            ...formData,
            phone: e.target.value
          })} radius="md" data-id="element-477" />

            <TextInput label="Email (Optional)" placeholder="email@example.com" leftSection={<Mail size={16} color="#64748b" data-id="element-480" />} value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} radius="md" data-id="element-479" />

            <Button fullWidth color="green" radius="md" size="md" leftSection={<Send size={18} data-id="element-482" />} onClick={handleSubmit} disabled={!formData.name || !formData.phone} styles={{
            root: {
              backgroundColor: '#10b981',
              '&:hover': {
                backgroundColor: '#059669'
              },
              '&:disabled': {
                backgroundColor: '#d1d5db'
              }
            }
          }} data-id="element-481">
              Submit Referral
            </Button>
          </Stack>
        </Card>

        {/* Progress Funnel */}
        <Stack gap="sm" data-id="element-483">
          <Text fw={700} size="md" c="#1a2332" data-id="element-484">
            Referral Pipeline
          </Text>

          <Card p="lg" radius="md" withBorder data-id="element-485">
            <Stepper active={-1} orientation="vertical" size="sm" styles={{
            step: {
              minHeight: 60
            },
            stepIcon: {
              backgroundColor: '#f1f5f9',
              border: 'none'
            },
            stepCompletedIcon: {
              backgroundColor: '#10b981'
            },
            verticalSeparator: {
              backgroundColor: '#e2e8f0'
            }
          }} data-id="element-486">
              {referralsByStage.map((item, index) => <Stepper.Step key={item.stage} label={<Group justify="space-between" style={{
              width: '100%'
            }} data-id="element-488">
                      <Text fw={500} size="sm" c="#1a2332" data-id="element-489">
                        {item.label}
                      </Text>
                      <Box px="sm" py={4} style={{
                backgroundColor: item.count > 0 ? '#d1fae5' : '#f1f5f9',
                borderRadius: 12
              }} data-id="element-490">
                        <Text fw={600} size="sm" c={item.count > 0 ? '#10b981' : '#64748b'} data-id="element-491">
                          {item.count}
                        </Text>
                      </Box>
                    </Group>} icon={<Text fw={600} size="xs" c="#64748b" data-id="element-492">
                      {index + 1}
                    </Text>} data-id="element-487" />)}
            </Stepper>
          </Card>
        </Stack>

        {/* Recent Referrals */}
        <Stack gap="sm" data-id="element-493">
          <Text fw={700} size="md" c="#1a2332" data-id="element-494">
            Your Referrals
          </Text>

          <Stack gap="sm" data-id="element-495">
            {mockReferrals.map(referral => <Card key={referral.id} p="md" radius="md" withBorder data-id="element-496">
                <Group justify="space-between" align="flex-start" data-id="element-497">
                  <Stack gap={4} data-id="element-498">
                    <Text fw={600} size="sm" c="#1a2332" data-id="element-499">
                      {referral.name}
                    </Text>
                    <Text size="xs" c="#64748b" data-id="element-500">
                      {referral.phone}
                    </Text>
                    <Text size="xs" c="#94a3b8" data-id="element-501">
                      Submitted:{' '}
                      {new Date(referral.submittedAt).toLocaleDateString()}
                    </Text>
                  </Stack>
                  <StatusBadge status={referral.stage} data-id="element-502" />
                </Group>
              </Card>)}
          </Stack>
        </Stack>
      </Stack>
    </ScrollArea>;
}